import React, { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { 
  Popconfirm, 
  Card, 
  Select, 
  Input, 
  InputNumber, 
  Typography, 
  Button, 
  Tabs, 
  Space, 
  Tooltip, 
  Tag,
  Switch,
  Radio,
  Checkbox,
  Collapse,
  Alert
} from 'antd';
import { 
  DeleteOutlined, 
  SearchOutlined, 
  SettingOutlined, 
  InfoCircleOutlined,
  PlusOutlined,
  MinusCircleOutlined, 
  GlobalOutlined, 
  CodeOutlined, 
} from '@ant-design/icons';
import { Icon } from '@iconify/react';
import GenericNodeContent from '../common/GenericNodeContent';
import NodeInputHandler from '../common/NodeInputHandler';

const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const ResearchNode = ({ id, data, isConnectable }) => {
  // Initialize state with backwards compatibility
  const [nodeName, setNodeName] = useState(data.nodeName || `Research ${id}`);
  const [activeTab, setActiveTab] = useState('prompt');
  const [advancedMode, setAdvancedMode] = useState(false);

  // Helper function to map legacy models to new ones
  const getLegacyModelMapping = (oldModel) => {
    const modelMapping = {
      'llama-3.1-sonar-small-128k-online': 'sonar',
      'llama-3.1-sonar-large-128k-online': 'sonar-pro',
      'llama-3.1-sonar-huge-128k-online': 'sonar-pro',
      'llama-3.1-sonar-small-128k-chat': 'sonar',
      'llama-3.1-sonar-large-128k-chat': 'sonar-pro'
    };
    return modelMapping[oldModel] || oldModel;
  };

  // Initialize settings with backwards compatibility
  const [settings, setSettings] = useState({
    model: getLegacyModelMapping(data.model) || 'sonar',
    temperature: data.temperature || 1.0,
    searchContext: data.searchContext !== false,
    citations: data.citations !== false,
    searchDomainFilters: data.searchDomainFilters || [],
    citationQuality: data.citationQuality || 'standard',
    frequency_penalty: data.frequency_penalty || 0,
    presence_penalty: data.presence_penalty || 0,
    top_p: data.top_p || 1,
    top_k: data.top_k || 0,
    search_recency_filter: data.search_recency_filter || 'any'
  });


  // Available models with descriptions
  const modelOptions = [
    {
      value: 'sonar',
      label: 'Sonar',
      description: 'Fast, efficient model with real-time web search capability'
    },
    {
      value: 'sonar-pro',
      label: 'Sonar Pro',
      description: 'Enhanced model with better citations and complex query support'
    },
    {
      value: 'sonar-reasoning',
      label: 'Sonar Reasoning',
      description: 'Lightweight reasoning offering powered by reasoning models trained with DeepSeek R1.'
    },
    {
      value: 'sonar-reasoning-pro',
      label: 'Sonar Reasoning Pro',
      description: 'Premier reasoning offering powered by DeepSeek R1'
    },
    {
      value: 'sonar-deep-research',
      label: 'Sonar Deep Research',
      description: 'Deep Research conducts comprehensive, expert-level research and synthesizes it into accessible, actionable reports.'
    },

  ];

  useEffect(() => {
    const nodeData = {
        ...settings,
        nodeName
    };
    
    data.updateNodeData(id, nodeData);
}, [settings, nodeName, id, data]);


  const handleModelChange = (value) => {
    setSettings(prev => ({ 
      ...prev, 
      model: value,
      maxTokens: value === 'sonar-pro' ? Math.min(prev.maxTokens, 8192) : prev.maxTokens
    }));
  };

  const handleNameChange = (e) => {
    setNodeName(e.target.value);
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleContentChange = (nodeId, newData) => {
    data.updateNodeData(nodeId, newData);
  };

  const handleDomainAdd = (domain) => {
    if (domain && !settings.searchDomainFilters.includes(domain)) {
      handleSettingChange('searchDomainFilters', [...settings.searchDomainFilters, domain]);
    }
  };

  const handleDomainRemove = (domain) => {
    handleSettingChange(
      'searchDomainFilters', 
      settings.searchDomainFilters.filter(d => d !== domain)
    );
  };

  const renderPromptTab = () => (
    <TabPane tab="Prompt" key="prompt">
      <Space direction="vertical" style={{ width: '100%' }}>
        <GenericNodeContent
          id={id}
          data={data}
          onChange={handleContentChange}
          placeholder="Enter your research query..."
        >
        </GenericNodeContent>

        <Collapse ghost style={{ marginTop: 8 }}>
          {/* Search Settings Panel */}
          <Panel 
            header={
              <Space>
                <SearchOutlined />
                <Text strong>Search Settings</Text>
              </Space>
            } 
            key="search"
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Space align="center">
                  <Switch
                    checked={settings.searchContext}
                    onChange={(value) => handleSettingChange('searchContext', value)}
                  />
                  <Text>Enable Real-time Search</Text>
                </Space>
              </div>

              <div>
                <Space align="center">
                  <Switch
                    checked={settings.citations}
                    onChange={(value) => handleSettingChange('citations', value)}
                  />
                  <Text>Include Citations</Text>
                </Space>
              </div>

              <div>
                <Text strong>Search Recency</Text>
                <Select
                  style={{ width: '100%', marginTop: 4 }}
                  value={settings.search_recency_filter}
                  onChange={(value) => handleSettingChange('search_recency_filter', value)}
                  className="nodrag"
                >
                  <Option value="any">Any time</Option>
                  <Option value="day">Past 24 hours</Option>
                  <Option value="week">Past week</Option>
                  <Option value="month">Past month</Option>
                  <Option value="year">Past year</Option>
                </Select>
              </div>
            </Space>
          </Panel>

          {/* Domain Filters Panel */}
          <Panel 
            header={
              <Space>
                <GlobalOutlined />
                <Text strong>Domain Filters</Text>
              </Space>
            } 
            key="domains"
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Input.Search
                placeholder="Add domain (e.g., wikipedia.org)"
                enterButton="Add"
                onSearch={handleDomainAdd}
                className="nodrag"
              />
              <div style={{ marginTop: 8 }}>
                {settings.searchDomainFilters.map(domain => (
                  <Tag
                    key={domain}
                    closable
                    onClose={() => handleDomainRemove(domain)}
                    style={{ margin: '4px' }}
                  >
                    {domain}
                  </Tag>
                ))}
              </div>
            </Space>
          </Panel>

        </Collapse>
      </Space>
    </TabPane>
  );

  const renderSettingsTab = () => (
    <TabPane tab="Settings" key="settings">
      <Space direction="vertical" style={{ width: '100%' }}>
        {renderModelSelection()}
        
        <div>
          <Space align="center">
            <Text strong>Temperature</Text>
            <Tooltip title="Controls response randomness (0 = focused, 2 = creative)">
              <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
            </Tooltip>
          </Space>
          <InputNumber
            style={{ width: '100%', marginTop: 4 }}
            min={0}
            max={2}
            step={0.1}
            value={settings.temperature}
            onChange={(value) => handleSettingChange('temperature', value)}
            className="nodrag"
          />
        </div>

        {/* Advanced Settings Switch */}
        <div style={{ marginTop: 16 }}>
          <Space align="center">
            <Switch
              size="small"
              checked={advancedMode}
              onChange={setAdvancedMode}
            />
            <Text>Show Advanced Parameters</Text>
          </Space>
        </div>

        {/* Advanced Parameters Panel */}
        {advancedMode && (
          <Collapse ghost>
            <Panel header="Advanced Parameters" key="advanced">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Space align="center">
                    <Text strong>Top P</Text>
                    <Tooltip title="Cumulative probability for token sampling">
                      <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                    </Tooltip>
                  </Space>
                  <InputNumber
                    style={{ width: '100%', marginTop: 4 }}
                    min={0}
                    max={1}
                    step={0.1}
                    value={settings.top_p}
                    onChange={(value) => handleSettingChange('top_p', value)}
                    className="nodrag"
                  />
                </div>

                <div>
                  <Space align="center">
                    <Text strong>Frequency Penalty</Text>
                    <Tooltip title="Reduces repetition of frequent tokens. Cannot be used with Presence Penalty.">
                      <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                    </Tooltip>
                  </Space>
                  <InputNumber
                    style={{ width: '100%', marginTop: 4 }}
                    min={-1.999}
                    max={1.999}
                    step={0.1}
                    value={settings.frequency_penalty}
                    onChange={(value) => {
                      // If setting frequency penalty, reset presence penalty
                      if (value !== null && value > 0) {
                        handleSettingChange('presence_penalty', 0);
                      }
                      handleSettingChange('frequency_penalty', value || 0);
                    }}
                    disabled={settings.presence_penalty > 0}
                    className="nodrag"
                  />
                </div>

                <div>
                  <Space align="center">
                    <Text strong>Presence Penalty</Text>
                    <Tooltip title="Reduces repetition of all tokens. Cannot be used with Frequency Penalty.">
                      <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                    </Tooltip>
                  </Space>
                  <InputNumber
                    style={{ width: '100%', marginTop: 4 }}
                    min={-2.0}
                    max={1.999}
                    step={0.1}
                    value={settings.presence_penalty}
                    onChange={(value) => {
                      // If setting presence penalty, reset frequency penalty
                      if (value !== null && value > 0) {
                        handleSettingChange('frequency_penalty', 0);
                      }
                      handleSettingChange('presence_penalty', value || 0);
                    }}
                    disabled={settings.frequency_penalty > 0}
                    className="nodrag"
          />
        </div>
                {/* Add alert if both penalties are set */}
                {settings.frequency_penalty > 0 && settings.presence_penalty > 0 && (
          <Alert
            type="warning"
            showIcon
            message="Only one penalty type can be active at a time."
            style={{ marginTop: 8 }}
          />
        )}
              </Space>
            </Panel>
          </Collapse>
        )}
      </Space>
    </TabPane>
  );

  // Render functions for different sections
  const renderModelSelection = () => (
    <div>
      <Text strong>Model</Text>
      <Select
        style={{ width: '100%', marginTop: 4 }}
        value={settings.model}
        onChange={handleModelChange}
        className="nodrag"
        dropdownMatchSelectWidth={false}
        dropdownStyle={{ minWidth: '300px' }}
        optionLabelProp="label"
      >
        {modelOptions.map(model => (
          <Option key={model.value} value={model.value} label={model.label}>
            <div style={{ padding: '4px 0' }}>
              <div style={{ fontWeight: 500 }}>{model.label}</div>
              <div style={{ fontSize: '12px', color: '#888', whiteSpace: 'normal', paddingRight: '12px' }}>
                {model.description}
              </div>
            </div>
          </Option>
        ))}
      </Select>
    </div>
  );


  const renderOutputTab = () => (
    <TabPane tab="Output" key="output">
      <Space direction="vertical" style={{ width: '100%' }}>
        <Text type="secondary">
          Available outputs from this node:
        </Text>
        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '12px', 
          borderRadius: '6px',
          marginTop: '8px'
        }}>
          <Space direction="vertical">
            <div>
              <Text strong>Research Results</Text>
              <div>
                <Text code>{`[[content] from ${id}]`}</Text>
              </div>
            </div>

            {settings.citations && (
              <div>
                <Text strong>Citations</Text>
                <div>
                  <Text code>{`[[citations] from ${id}]`}</Text>
                </div>
              </div>
            )}

            {settings.searchContext && (
              <div>
                <Text strong>Search Context</Text>
                <div>
                  <Text code>{`[[search_context] from ${id}]`}</Text>
                </div>
              </div>
            )}
          </Space>
        </div>
      </Space>
    </TabPane>
);

  return (
    <Card 
      title={
        <Space>
          <SearchOutlined style={{ color: '#1890ff' }}/>
          <span>Research</span>
          <Tag color="blue">
            {settings.model === 'sonar-pro' ? 'Sonar Pro' : 
             settings.model === 'sonar-reasoning' ? 'Sonar Reasoning' : 
             settings.model === 'sonar-reasoning-pro' ? 'Sonar Reasoning Pro' : 
             settings.model === 'sonar-deep-research' ? 'Sonar Deep Research' :
             'Sonar'}
          </Tag>
        </Space>
      }
      style={{ width: 380 }}
      extra={
        <Space>
          <Button 
            icon={<SettingOutlined />} 
            type="text"
            onClick={() => setActiveTab('settings')}
          />
          <Popconfirm
            title="Delete Node"
            description="Are you sure you want to delete this node?"
            onConfirm={() => data.onDelete(id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      }
    >
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
      
      <Input
        placeholder="Node Name"
        value={nodeName}
        onChange={handleNameChange}
        style={{ marginBottom: 16 }}
        prefix={<InfoCircleOutlined style={{ color: '#bfbfbf' }} />}
        className="nodrag"
      />

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {renderPromptTab()}
        {renderSettingsTab()}
        {renderOutputTab()}
      </Tabs>

      <Handle type="source" position={Position.Right} id="a" isConnectable={isConnectable} />
    </Card>
  );
};

export default ResearchNode;